/* global __firebase_config, __app_id */ // ESLint directive to recognize __firebase_config and __app_id as global variables

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, query, serverTimestamp, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"; // Added deleteObject

// Helper function to convert basic Markdown to HTML
const convertMarkdownToHtml = (markdown) => {
  if (!markdown) return '';

  let html = markdown;

  // Replace bold text (e.g., **text**)
  // eslint-disable-next-line no-useless-escape
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Replace italic text (e.g., *text* or _text_)
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');

  // Replace headings (e.g., # Heading, ## Subheading)
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');

  // Replace unordered lists (e.g., - item or * item)
  html = html.replace(/^\s*[\*-] (.*$)/gm, '<li>$1</li>');
  if (html.includes('<li>')) {
    html = `<ul>${html}</ul>`;
    // Clean up multiple ul tags if they appear due to multiple list blocks
    html = html.replace(/<\/ul>\s*<ul>/g, '');
  }

  // Replace ordered lists (e.g., 1. item)
  html = html.replace(/^\s*\d+\. (.*$)/gm, '<li>$1</li>');
  if (html.includes('<li>') && !html.includes('<ul>')) { // Only if not already an unordered list
    html = `<ol>${html}</ol>`;
    // Clean up multiple ol tags
    html = html.replace(/<\/ol>\s*<ol>/g, '');
  }

  // Replace newlines with <br/> for paragraphs, but not within list items or headings already processed
  html = html.replace(/(?<!<\/li>)(?<!<\/h[1-6]>)\n/g, '<br/>');

  return html;
};

// Component for displaying Authentication Status
const AuthStatusDisplay = ({ userId, userDisplayName, handleGoogleSignIn, handleSignOut, isAuthReady, isAllowedToModify }) => {
  return (
    <div className="text-sm text-gray-600 text-center mb-4 break-all">
      {isAuthReady ? (
        userId ? (
          <>
            <p>Logged in as: <span className="font-semibold text-blue-700">{userDisplayName || 'Anonymous User'}</span></p>
            <p className="text-xs">User ID: <span className="font-mono text-blue-700">{userId}</span></p>
            {isAllowedToModify && <p className="text-xs text-green-600 font-semibold">You have write permissions.</p>}
            <button
              onClick={handleSignOut}
              className="mt-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 text-sm"
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
            <p>Admins log in for Maintenance.</p>
            <button
              onClick={handleGoogleSignIn}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 text-sm"
            >
              Sign in with Google
            </button>
          </>
        )
      ) : (
        <p>Initializing authentication...</p>
      )}
    </div>
  );
};

// Component for the Repair Assistant Section
const RepairAssistantSection = ({ device, setDevice, problem, setProblem, handleSubmit, isLoading, error, response, isAuthReady }) => {
  return (
    <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="device" className="block text-gray-700 text-sm font-medium mb-1">
            Electronic Test Equipment (Make & Model):
          </label>
          <input
            type="text"
            id="device"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            placeholder="e.g., Agilent E4407B ESA-E Spectrum Analyzer or '6448B'"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
            required
          />
        </div>

        <div>
          <label htmlFor="problem" className="block text-gray-700 text-sm font-medium mb-1">
            Problem Description:
          </label>
          <textarea
            id="problem"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            placeholder="e.g., No RF input signal detected, Power supply unit not turning on"
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 resize-y"
            required
          ></textarea>
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-md font-semibold text-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading || !isAuthReady}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating Suggestions...
            </div>
          ) : (
            'Get Intelligent Repair Suggestions'
          )}
        </button>
      </form>

      {error && (
        <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{error}</p>
        </div>
      )}

      {response && (
        <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200 shadow-inner">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">Repair Suggestions:</h2>
          <div
            className="prose prose-blue max-w-none text-gray-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(response) }}
          />
        </div>
      )}
    </div>
  );
};

// Component for the Equipment Database Section
const EquipmentDatabaseSection = ({
  equipmentError,
  editMode,
  handleUpdateEquipment,
  handleAddEquipment,
  newEquipmentName, setNewEquipmentName,
  newEquipmentDescription, setNewEquipmentDescription,
  newEquipmentImageUrl, setNewEquipmentImageUrl,
  newEquipmentManufacturer, setNewEquipmentManufacturer,
  newEquipmentModel, setNewEquipmentModel,
  newEquipmentType, setNewEquipmentType,
  newEquipmentSpecifications, setNewEquipmentSpecifications,
  newEquipmentServiceManualUrl, setNewEquipmentServiceManualUrl,
  isAddingEquipment,
  isAuthReady,
  handleImageUpload, // Still needed for main image upload
  isUploadingImage,
  uploadError,
  cancelEditMode,
  equipmentList,
  startEditMode,
  handleDeleteEquipment,
  searchManufacturer, setSearchManufacturer,
  searchModel, setSearchModel,
  searchType, setSearchType,
  isAllowedToModify,
  openImageModal // Pass openImageModal prop
}) => {
  return (
    <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">

      {equipmentError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{equipmentError}</p>
        </div>
      )}

      {/* Add/Edit Equipment Form - Conditionally rendered/disabled */}
      {isAllowedToModify ? (
        <form onSubmit={editMode ? handleUpdateEquipment : handleAddEquipment} className="space-y-4 mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            {editMode ? 'Edit Equipment' : 'Add New Equipment'}
          </h3>
          <div>
            <label htmlFor="newEquipmentName" className="block text-gray-700 text-sm font-medium mb-1">
              Equipment Name:
            </label>
            <input
              type="text"
              id="newEquipmentName"
              value={newEquipmentName}
              onChange={(e) => setNewEquipmentName(e.target.value)}
              placeholder="e.g., Soldering Iron, Multimeter"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="newEquipmentManufacturer" className="block text-gray-700 text-sm font-medium mb-1">
              Manufacturer (Optional):
            </label>
            <input
              type="text"
              id="newEquipmentManufacturer"
              value={newEquipmentManufacturer}
              onChange={(e) => setNewEquipmentManufacturer(e.target.value)}
              placeholder="e.g., Fluke, Weller"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
          </div>
          <div>
            <label htmlFor="newEquipmentModel" className="block text-gray-700 text-sm font-medium mb-1">
              Model (Optional):
            </label>
            <input
              type="text"
              id="newEquipmentModel"
              value={newEquipmentModel}
              onChange={(e) => setNewEquipmentModel(e.target.value)}
              placeholder="e.g., 117, WESD51"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
          </div>
          <div>
            <label htmlFor="newEquipmentType" className="block text-gray-700 text-sm font-medium mb-1">
              Type (Optional):
            </label>
            <input
              type="text"
              id="newEquipmentType"
              value={newEquipmentType}
              onChange={(e) => setNewEquipmentType(e.target.value)}
              placeholder="e.g., Multimeter, Soldering Station"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
          </div>
          <div>
            <label htmlFor="newEquipmentSpecifications" className="block text-gray-700 text-sm font-medium mb-1">
              Specifications (Optional):
            </label>
            <textarea
              id="newEquipmentSpecifications"
              value={newEquipmentSpecifications}
              onChange={(e) => setNewEquipmentSpecifications(e.target.value)}
              placeholder="e.g., True-RMS, 600V AC/DC; 60W, digital display"
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200 resize-y"
            ></textarea>
          </div>
          <div>
            <label htmlFor="newEquipmentDescription" className="block text-gray-700 text-sm font-medium mb-1">
              General Description (Optional):
            </label>
            <textarea
              id="newEquipmentDescription"
              value={newEquipmentDescription}
              onChange={(e) => setNewEquipmentDescription(e.target.value)}
              placeholder="e.g., A reliable tool for general electrical measurements."
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200 resize-y"
            ></textarea>
          </div>
          <div>
            <label htmlFor="newEquipmentImageUrl" className="block text-gray-700 text-sm font-medium mb-1">
              Main Equipment Image URL (Optional):
            </label>
            <input
              type="url"
              id="newEquipmentImageUrl"
              value={newEquipmentImageUrl}
              onChange={(e) => setNewEquipmentImageUrl(e.target.value)}
              placeholder="e.g., https://placehold.co/150x150"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
          </div>
          {/* Main Image Upload Section - Still uses file upload */}
          <div>
            <label htmlFor="imageUpload" className="block text-gray-700 text-sm font-medium mb-1">
              Upload Main Image (Optional):
            </label>
            <input
              type="file"
              id="imageUpload"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'main')} // Pass type to handler
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
            {isUploadingImage && (
              <div className="flex items-center justify-center mt-2 text-green-600">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Uploading image...
              </div>
            )}
            {uploadError && (
              <p className="text-red-500 text-sm mt-2">{uploadError}</p>
            )}
            {newEquipmentImageUrl && !isUploadingImage && !uploadError && (
              <div className="mt-2 text-sm text-gray-600 flex items-center">
                Main image selected/uploaded: <img src={newEquipmentImageUrl} alt="Preview" className="w-10 h-10 object-cover rounded-sm ml-2"/>
              </div>
            )}
          </div>

          {/* New: Service Manual URL Field */}
          <div>
            <label htmlFor="newEquipmentServiceManualUrl" className="block text-gray-700 text-sm font-medium mb-1">
              Service Manual PDF URL (Optional):
            </label>
            <input
              type="url"
              id="newEquipmentServiceManualUrl"
              value={newEquipmentServiceManualUrl}
              onChange={(e) => setNewEquipmentServiceManualUrl(e.target.value)}
              placeholder="e.g., https://example.com/manual.pdf"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-200"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 text-white py-3 rounded-md font-semibold text-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAddingEquipment || !isAuthReady}
          >
            {isAddingEquipment ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Uploading image...
              </div>
            ) : (
              editMode ? 'Update Equipment' : 'Add Equipment'
            )}
          </button>
          {editMode && (
            <button
              type="button"
              onClick={cancelEditMode}
              className="w-full bg-gray-500 text-white py-3 rounded-md font-semibold text-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 mt-2"
            >
              Cancel Edit
            </button>
          )}
        </form>
      ) : (
        <div className="mb-8 p-4 border border-gray-200 rounded-md bg-yellow-50 text-yellow-800">
          <p className="font-semibold"></p>
        </div>
      )}

      {/* Search Filters */}
      <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50 space-y-3">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Filter Equipment</h3>
        <div>
          <label htmlFor="searchManufacturer" className="block text-gray-700 text-sm font-medium mb-1">
            Search by Manufacturer:
          </label>
          <input
            type="text"
            id="searchManufacturer"
            value={searchManufacturer}
            onChange={(e) => setSearchManufacturer(e.target.value)}
            placeholder="e.g., Fluke"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
          />
        </div>
        <div>
          <label htmlFor="searchModel" className="block text-gray-700 text-sm font-medium mb-1">
            Search by Model:
          </label>
          <input
            type="text"
            id="searchModel"
            value={searchModel}
            onChange={(e) => setSearchModel(e.target.value)}
            placeholder="e.g., 117"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
            />
        </div>
        <div>
          <label htmlFor="searchType" className="block text-gray-700 text-sm font-medium mb-1">
            Search by Type:
          </label>
            <input
              type="text"
              id="searchType"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              placeholder="e.g., Multimeter"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
            />
        </div>
      </div>


      {/* Equipment List */}
      <h3 className="text-xl font-semibold text-gray-700 mb-3">On the Bench</h3>
      {equipmentList.length === 0 ? (
        <p className="text-gray-600 italic">No equipment added yet. Add some above!</p>
      ) : (
        <div className="flex flex-col gap-4">
          {equipmentList.map((equipment) => (
            <div key={equipment.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center text-left">
                {/* Main Image Column */}
                <div className="flex justify-center md:justify-start items-center md:col-span-1">
                  <p className="text-sm font-bold text-gray-800 mr-2">Image:</p>
                  <div
                    className="w-full max-w-xl h-auto bg-gray-100 flex items-center justify-center rounded-md overflow-hidden border border-gray-200 cursor-pointer"
                    onClick={() => openImageModal(equipment.imageUrl, equipment.name)} // Open modal on click
                  >
                    <img
                      src={equipment.imageUrl}
                      alt={equipment.name}
                      className="w-full h-auto object-contain" // Removed hover scale
                      onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/768x322/aabbcc/ffffff?text=${encodeURIComponent(equipment.name.substring(0, 10))}`; }}
                    />
                  </div>
                </div>
                {/* Details Columns */}
                <div className="md:col-span-1">
                  <p className="text-lg font-bold text-gray-800 mb-1"><strong>Equipment:</strong> {equipment.name}</p>
                  {equipment.manufacturer && <p className="text-sm text-gray-700"><strong>Manufacturer:</strong> {equipment.manufacturer}</p>}
                  {equipment.model && <p className="text-sm text-gray-700"><strong>Model:</strong> {equipment.model}</p>}
                  {equipment.type && <p className="text-sm text-gray-700"><strong>Type:</strong> {equipment.type}</p>}
                  {equipment.specifications && <p className="text-sm text-gray-700"><strong>Specifications:</strong> {equipment.specifications}</p>}
                  {equipment.description && (
                    <p className="text-sm text-gray-600 mt-1"><strong>General Description:</strong> {equipment.description}</p>
                  )}
                  {/* Service Manual Link */}
                  {equipment.serviceManualUrl && (
                    <div className="mt-2">
                      <a
                        href={equipment.serviceManualUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 transition duration-200 shadow-md"
                        title="View Service Manual (PDF)"
                      >
                        {/* PDF Icon (SVG) - Phosphor icons or similar style */}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-5 h-5 mr-2">
                          <path d="M216,224H40a16,16,0,0,1-16-16V48A16,16,0,0,1,40,32H168.49a16,16,0,0,1,11.32,4.69L219.31,67.19A16,16,0,0,1,224,78.68V216A16,16,0,0,1,216,224ZM40,48V208H216V78.68L185.31,48H40ZM128,120a8,8,0,0,0-8,8v32a8,8,0,0,0,16,0V128A8,8,0,0,0,128,120ZM168,120a8,8,0,0,0-8,8v32a8,8,0,0,0,16,0V128A8,8,0,0,0,168,120ZM88,120a8,8,0,0,0-8,8v32a8,8,0,0,0,16,0V128A8,8,0,0,0,88,120Z" />
                        </svg>
                        Service Manual
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {isAllowedToModify && (
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    onClick={() => startEditMode(equipment)}
                    className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-200"
                    title="Edit Equipment Details"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.646 3.646l-2.828 2.828-1.414-1.414 2.828-2.828 1.414 1.414zm-5.657 5.657a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l1.414-1.414 4 4zM16 17H4v-2h12v2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteEquipment(equipment.id)}
                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200"
                    title="Delete Equipment"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm6 3a1 1 0 100 2v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for the Failed Components Database Section
const FailedComponentsSection = ({
  failedComponentsError,
  editModeFailedComponent,
  handleUpdateFailedComponent,
  handleAddFailedComponent,
  newFailedComponentImageUrl, setNewFailedComponentImageUrl,
  newFailedComponentDescription, setNewFailedComponentDescription,
  isAddingFailedComponent,
  isAuthReady,
  handleFailedComponentImageUpload, // Specific handler for file upload
  isUploadingFailedComponentImage,
  uploadFailedComponentError,
  cancelEditModeFailedComponent,
  failedComponentsList,
  startEditModeFailedComponent,
  handleDeleteFailedComponent,
  isAllowedToModify,
  openImageModal // Pass openImageModal prop
}) => {
  return (
    <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">

      {failedComponentsError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{failedComponentsError}</p>
        </div>
      )}

      {/* Add/Edit Failed Component Form - Conditionally rendered/disabled */}
      {isAllowedToModify ? (
        <form onSubmit={editModeFailedComponent ? handleUpdateFailedComponent : handleAddFailedComponent} className="space-y-4 mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            {editModeFailedComponent ? 'Edit Failed Component' : 'Add New Failed Component'}
          </h3>
          <div>
            <label htmlFor="failedComponentImageUpload" className="block text-gray-700 text-sm font-medium mb-1">
              Upload Failed Component Image:
            </label>
            <input
              type="file"
              id="failedComponentImageUpload"
              accept="image/*"
              onChange={handleFailedComponentImageUpload}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-200"
              required={!editModeFailedComponent || !newFailedComponentImageUrl} // Make required unless editing and image already exists
            />
            {isUploadingFailedComponentImage && (
              <div className="flex items-center justify-center mt-2 text-purple-600">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Uploading image...
              </div>
            )}
            {uploadFailedComponentError && (
              <p className="text-red-500 text-sm mt-2">{uploadFailedComponentError}</p>
            )}
            {newFailedComponentImageUrl && !isUploadingFailedComponentImage && !uploadFailedComponentError && (
              <div className="mt-2 text-sm text-gray-600 flex items-center">
                Main image selected/uploaded: <img src={newFailedComponentImageUrl} alt="Preview" className="w-10 h-10 object-cover rounded-sm ml-2"/>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="newFailedComponentDescription" className="block text-gray-700 text-sm font-medium mb-1">
              Description (Optional):
            </label>
            <textarea
              id="newFailedComponentDescription"
              value={newFailedComponentDescription}
              onChange={(e) => setNewFailedComponentDescription(e.target.value)}
              placeholder="e.g., Capacitor exploded due to overvoltage."
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-200 resize-y"
            ></textarea>
          </div>

          <button
            type="submit"
            className="w-full bg-purple-600 text-white py-3 rounded-md font-semibold text-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAddingFailedComponent || !isAuthReady || (!editModeFailedComponent && !newFailedComponentImageUrl)} // Disable if no image for new entry
          >
            {isAddingFailedComponent ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Uploading image...
              </div>
            ) : (
              editModeFailedComponent ? 'Update Component' : 'Add Component'
            )}
          </button>
          {editModeFailedComponent && (
            <button
              type="button"
              onClick={cancelEditModeFailedComponent}
              className="w-full bg-gray-500 text-white py-3 rounded-md font-semibold text-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 mt-2"
            >
              Cancel Edit
            </button>
          )}
        </form>
      ) : (
        <div className="mb-8 p-4 border border-gray-200 rounded-md bg-yellow-50 text-yellow-800">
          <p className="font-semibold"></p>
        </div>
      )}

      {/* Failed Components List */}
      <h3 className="text-xl font-semibold text-gray-700 mb-3">Headscratcher's Gallery</h3>
      {failedComponentsList.length === 0 ? (
        <p className="text-gray-600 italic">No failed components added yet. Add some above!</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {failedComponentsList.map((component) => (
            <div key={component.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 flex flex-col items-center relative">
              <div
                className="w-full max-w-md h-auto bg-gray-100 flex items-center justify-center rounded-md overflow-hidden border border-gray-200 mb-2 cursor-pointer"
                onClick={() => openImageModal(component.imageUrl, component.description || 'Failed Component')} // Open modal on click
              >
                <img
                  src={component.imageUrl}
                  alt={component.description || 'Failed Component'}
                  className="w-full h-auto object-contain" // Removed hover scale
                  onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/640x270/cccccc/333333?text=Failed`; }}
                />
              </div>
              {component.description && (
                <p className="text-sm text-gray-700 text-center">{component.description}</p>
              )}
              {isAllowedToModify && (
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    onClick={() => startEditModeFailedComponent(component)}
                    className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-200"
                    title="Edit Component"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.646 3.646l-2.828 2.828-1.414-1.414 2.828-2.828 1.414 1.414zm-5.657 5.657a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l1.414-1.414 4 4zM16 17H4v-2h12v2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteFailedComponent(component.id)}
                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200"
                    title="Delete Component"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm6 3a1 1 0 100 2v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// New BeforeAfterGallerySection Component
const BeforeAfterGallerySection = ({
  db,
  userId,
  isAllowedToModify,
  openImageModal,
  storage,
  isAuthReady // Pass isAuthReady to enable/disable form
}) => {
  const [beforeAfterRepairs, setBeforeAfterRepairs] = useState([]);
  const [newBeforeImageUrl, setNewBeforeImageUrl] = useState('');
  const [newAfterImageUrl, setNewAfterImageUrl] = useState('');
  const [newRepairDescription, setNewRepairDescription] = useState('');
  const [isUploadingBefore, setIsUploadingBefore] = useState(false);
  const [uploadBeforeError, setUploadBeforeError] = useState(null);
  const [isUploadingAfter, setIsUploadingAfter] = useState(false);
  const [uploadAfterError, setUploadAfterError] = useState(null);
  const [isAddingRepair, setIsAddingRepair] = useState(false);
  const [repairError, setRepairError] = useState(null);
  const [editModeRepair, setEditModeRepair] = useState(false);
  const [currentRepairId, setCurrentRepairId] = useState(null);

  // Fetch before/after repairs
  useEffect(() => {
    if (db) {
      const collectionAppId = db.app.options.projectId;
      const repairsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/before_after_repairs`);
      const q = query(repairsCollectionRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allRepairs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        allRepairs.sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));
        setBeforeAfterRepairs(allRepairs);
      }, (err) => {
        console.error("Error fetching before/after repairs:", err);
        setRepairError(`Failed to load repair entries: ${err.message}`);
      });

      return () => unsubscribe();
    }
  }, [db]);

  // Handle image upload for before/after repairs
  const handleRepairImageUpload = useCallback(async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAllowedToModify) {
      if (imageType === 'before') setUploadBeforeError("You do not have permission to upload images.");
      else if (imageType === 'after') setUploadAfterError("You do not have permission to upload images.");
      return;
    }

    let setIsUploading, setUploadErrorState, setImageUrlState;
    let storagePath;

    if (imageType === 'before') {
      setIsUploading = setIsUploadingBefore;
      setUploadErrorState = setUploadBeforeError;
      setImageUrlState = setNewBeforeImageUrl;
      storagePath = `artifacts/${db.app.options.projectId}/public/data/repair_images/${userId}/before_${file.name}`;
    } else if (imageType === 'after') {
      setIsUploading = setIsUploadingAfter;
      setUploadErrorState = setUploadAfterError;
      setImageUrlState = setNewAfterImageUrl;
      storagePath = `artifacts/${db.app.options.projectId}/public/data/repair_images/${userId}/after_${file.name}`;
    } else {
      console.error("Invalid imageType provided to handleRepairImageUpload:", imageType);
      return;
    }

    setIsUploading(true);
    setUploadErrorState(null);

    const imageRef = ref(storage, storagePath);

    try {
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      setImageUrlState(downloadURL);
      setUploadErrorState(null);
    } catch (err) {
      console.error(`Error uploading ${imageType} repair image:`, err);
      setUploadErrorState(`Image upload failed: ${err.message}. Make sure you are signed in.`);
      setImageUrlState('');
    } finally {
      setIsUploading(false);
    }
  }, [db, userId, storage, isAllowedToModify, setNewBeforeImageUrl, setNewAfterImageUrl, setIsUploadingBefore, setUploadBeforeError, setIsUploadingAfter, setUploadAfterError]);

  // Add new repair entry
  const handleAddRepair = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingRepair(true);
    setRepairError(null);

    if (!isAllowedToModify) {
      setRepairError("You do not have permission to add repair entries.");
      setIsAddingRepair(false);
      return;
    }

    if (!newBeforeImageUrl || !newAfterImageUrl) {
      setRepairError("Both Before and After images are required for a new repair entry.");
      setIsAddingRepair(false);
      return;
    }

    try {
      const collectionAppId = db.app.options.projectId;
      const repairsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/before_after_repairs`);

      await addDoc(repairsCollectionRef, {
        beforeImageUrl: newBeforeImageUrl,
        afterImageUrl: newAfterImageUrl,
        description: newRepairDescription,
        timestamp: serverTimestamp(),
        createdBy: userId
      });
      setNewBeforeImageUrl('');
      setNewAfterImageUrl('');
      setNewRepairDescription('');
    } catch (err) {
      console.error("Error adding repair entry:", err);
      setRepairError(`Failed to add repair entry: ${err.message}`);
    } finally {
      setIsAddingRepair(false);
    }
  }, [db, userId, newBeforeImageUrl, newAfterImageUrl, newRepairDescription, isAllowedToModify]);

  // Update existing repair entry
  const handleUpdateRepair = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingRepair(true); // Reusing state for loading
    setRepairError(null);

    if (!isAllowedToModify || !currentRepairId) {
      setRepairError("You do not have permission to update repair entries or no entry is selected.");
      setIsAddingRepair(false);
      return;
    }

    const repairToUpdate = beforeAfterRepairs.find(item => item.id === currentRepairId);
    if (!repairToUpdate || repairToUpdate.createdBy !== userId) {
        setRepairError("You can only update entries you have created.");
        setIsAddingRepair(false);
        return;
    }

    try {
      const collectionAppId = db.app.options.projectId;
      const repairDocRef = doc(db, `artifacts/${collectionAppId}/public/data/before_after_repairs`, currentRepairId);

      await updateDoc(repairDocRef, {
        beforeImageUrl: newBeforeImageUrl,
        afterImageUrl: newAfterImageUrl,
        description: newRepairDescription,
      });
      setNewBeforeImageUrl('');
      setNewAfterImageUrl('');
      setNewRepairDescription('');
      setEditModeRepair(false);
      setCurrentRepairId(null);
    } catch (err) {
      console.error("Error updating repair entry:", err);
      setRepairError(`Failed to update repair entry: ${err.message}`);
    } finally {
      setIsAddingRepair(false);
    }
  }, [db, userId, currentRepairId, newBeforeImageUrl, newAfterImageUrl, newRepairDescription, isAllowedToModify, beforeAfterRepairs]);

  // Start edit mode for a repair entry
  const startEditModeRepair = useCallback((repair) => {
    if (!isAllowedToModify || repair.createdBy !== userId) {
      setRepairError("You do not have permission to edit this repair entry.");
      return;
    }
    setEditModeRepair(true);
    setCurrentRepairId(repair.id);
    setNewBeforeImageUrl(repair.beforeImageUrl || '');
    setNewAfterImageUrl(repair.afterImageUrl || '');
    setNewRepairDescription(repair.description || '');
    setRepairError(null);
  }, [isAllowedToModify, userId]);

  // Cancel edit mode
  const cancelEditModeRepair = useCallback(() => {
    setEditModeRepair(false);
    setCurrentRepairId(null);
    setNewBeforeImageUrl('');
    setNewAfterImageUrl('');
    setNewRepairDescription('');
    setRepairError(null);
  }, []);

  // Delete repair entry
  const handleDeleteRepair = useCallback(async (id) => {
    if (!isAllowedToModify) {
      setRepairError("You do not have permission to delete repair entries.");
      return;
    }

    const repairToDelete = beforeAfterRepairs.find(item => item.id === id);
    if (!repairToDelete || repairToDelete.createdBy !== userId) {
        setRepairError("You can only delete entries you have created.");
        return;
    }

    if (window.confirm("Are you sure you want to delete this repair entry and its associated images?")) {
      try {
        const collectionAppId = db.app.options.projectId;
        const repairDocRef = doc(db, `artifacts/${collectionAppId}/public/data/before_after_repairs`, id);

        // Get the document to retrieve image URLs before deleting
        const docSnapshot = await db.getDoc(repairDocRef);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const imageUrlsToDelete = [];
          if (data.beforeImageUrl) imageUrlsToDelete.push(data.beforeImageUrl);
          if (data.afterImageUrl) imageUrlsToDelete.push(data.afterImageUrl);

          // Delete images from Storage
          const storageInstance = getStorage(db.app);
          for (const url of imageUrlsToDelete) {
            try {
              const imageRef = ref(storageInstance, url);
              await deleteObject(imageRef);
              console.log(`Deleted repair image from storage: ${url}`);
            } catch (storageErr) {
              console.warn(`Could not delete repair image ${url} from storage:`, storageErr);
            }
          }
        }

        await deleteDoc(repairDocRef);
        setRepairError(null);
      } catch (err) {
        console.error("Error deleting repair entry:", err);
        setRepairError(`Failed to delete repair entry: ${err.message}`);
      }
    }
  }, [db, userId, isAllowedToModify, beforeAfterRepairs]);


  return (
    <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-700 mb-3 text-center">Before & After Gallery</h3>

      {repairError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{repairError}</p>
        </div>
      )}

      {/* Add/Edit Repair Form */}
      {isAllowedToModify ? (
        <form onSubmit={editModeRepair ? handleUpdateRepair : handleAddRepair} className="space-y-4 mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-lg font-semibold text-gray-700 mb-3">
            {editModeRepair ? 'Edit Repair Entry' : 'Add New Repair Entry'}
          </h4>
          <div>
            <label htmlFor="repairDescription" className="block text-gray-700 text-sm font-medium mb-1">
              Repair Description (e.g., "Fluke 117 Multimeter Power Supply Repair"):
            </label>
            <input
              type="text"
              id="repairDescription"
              value={newRepairDescription}
              onChange={(e) => setNewRepairDescription(e.target.value)}
              placeholder="Description of the repair"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200"
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="beforeImageUpload" className="block text-gray-700 text-sm font-medium mb-1">
                Upload Before Image:
              </label>
              <input
                type="file"
                id="beforeImageUpload"
                accept="image/*"
                onChange={(e) => handleRepairImageUpload(e, 'before')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200"
                required={!editModeRepair || !newBeforeImageUrl}
              />
              {isUploadingBefore && <p className="text-indigo-600 text-sm mt-1">Uploading...</p>}
              {uploadBeforeError && <p className="text-red-500 text-sm mt-1">{uploadBeforeError}</p>}
              {newBeforeImageUrl && !isUploadingBefore && (
                <img src={newBeforeImageUrl} alt="Before Preview" className="w-24 h-24 object-cover rounded-md mt-2"/>
              )}
            </div>
            <div>
              <label htmlFor="afterImageUpload" className="block text-gray-700 text-sm font-medium mb-1">
                Upload After Image:
              </label>
              <input
                type="file"
                id="afterImageUpload"
                accept="image/*"
                onChange={(e) => handleRepairImageUpload(e, 'after')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-200"
                required={!editModeRepair || !newAfterImageUrl}
              />
              {isUploadingAfter && <p className="text-indigo-600 text-sm mt-1">Uploading...</p>}
              {uploadAfterError && <p className="text-red-500 text-sm mt-1">{uploadAfterError}</p>}
              {newAfterImageUrl && !isUploadingAfter && (
                <img src={newAfterImageUrl} alt="After Preview" className="w-24 h-24 object-cover rounded-md mt-2"/>
              )}
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-indigo-600 text-white py-3 rounded-md font-semibold text-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAddingRepair || !isAuthReady || isUploadingBefore || isUploadingAfter || (!editModeRepair && (!newBeforeImageUrl || !newAfterImageUrl))}
          >
            {isAddingRepair ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Saving Repair...
              </div>
            ) : (
              editModeRepair ? 'Update Repair Entry' : 'Add Repair Entry'
            )}
          </button>
          {editModeRepair && (
            <button
              type="button"
              onClick={cancelEditModeRepair}
              className="w-full bg-gray-500 text-white py-3 rounded-md font-semibold text-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 mt-2"
            >
              Cancel Edit
            </button>
          )}
        </form>
      ) : (
        <div className="mb-8 p-4 border border-gray-200 rounded-md bg-yellow-50 text-yellow-800">
          <p className="font-semibold"></p>
        </div>
      )}

      {/* List of Before & After Repairs */}
      {beforeAfterRepairs.length === 0 ? (
        <p className="text-gray-600 italic text-center">No repair entries added yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {beforeAfterRepairs.map((repair) => (
            <div key={repair.id} className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col items-center relative">
              <h4 className="text-lg font-bold text-gray-800 mb-2 text-center">{repair.description || 'Untitled Repair'}</h4>
              {/* Changed this div to use grid for side-by-side layout on sm screens and above */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                {repair.beforeImageUrl && (
                  <div
                    className="flex flex-col items-center cursor-pointer w-full"
                    onClick={() => openImageModal(repair.beforeImageUrl, `Before Repair: ${repair.description || 'Untitled'}`)}
                  >
                    <p className="text-sm font-bold text-gray-700 mb-1">Before:</p>
                    <img src={repair.beforeImageUrl} alt="Before Repair" className="w-full h-auto max-h-60 object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/300x150/cccccc/333333?text=Before`; }} />
                  </div>
                )}
                {repair.afterImageUrl && (
                  <div
                    className="flex flex-col items-center cursor-pointer w-full"
                    onClick={() => openImageModal(repair.afterImageUrl, `After Repair: ${repair.description || 'Untitled'}`)}
                  >
                    <p className="text-sm font-bold text-gray-700 mb-1">After:</p>
                    <img src={repair.afterImageUrl} alt="After Repair" className="w-full h-auto max-h-60 object-contain rounded-md border border-gray-300" onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/300x150/cccccc/333333?text=After`; }} />
                  </div>
                )}
              </div>
              {isAllowedToModify && (
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    onClick={() => startEditModeRepair(repair)}
                    className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-200"
                    title="Edit Repair"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.646 3.646l-2.828 2.828-1.414-1.414 2.828-2.828 1.414 1.414zm-5.657 5.657a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l1.414-1.414 4 4zM16 17H4v-2h12v2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteRepair(repair.id)}
                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200"
                    title="Delete Repair"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm6 3a1 1 0 100 2v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// New ImageModal Component
const ImageModal = ({ imageUrl, altText, onClose }) => {
  if (!imageUrl) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={onClose} // Close when clicking outside the image
    >
      <div className="relative max-w-full max-h-full flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-200 transition duration-200 z-50"
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={imageUrl}
          alt={altText}
          className="max-w-full max-h-full object-contain rounded-lg shadow-xl"
          onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/800x600/cccccc/333333?text=Image+Load+Error`; }}
        />
        {altText && (
          <p className="mt-4 text-white text-lg font-semibold text-center">{altText}</p>
        )}
      </div>
    </div>
  );
};

// New HistoricalFactsSection Component
const HistoricalFactsSection = ({ db, openImageModal, handleScrapeFacts, isScrapingFacts, scrapeFactsError, isAllowedToModify }) => {
  const [currentMonthYear, setCurrentMonthYear] = useState(new Date()); // Date object for calendar view
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD for fact display
  const [facts, setFacts] = useState([]);
  const [allFactDates, setAllFactDates] = new useState(new Set()); // Stores "MM-DD" for quick lookup
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFacts = useCallback(async () => {
    if (!db) return;

    setIsLoading(true);
    setFacts([]);
    setError(null);

    try {
      const collectionAppId = db.app.options.projectId;
      // Changed to 'historical_facts_test' to match where the Cloud Function writes
      const factsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/historical_facts_test`);

      const q = query(factsCollectionRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allFactsData = snapshot.docs.map(doc => {
          const data = doc.data();
          const mappedData = {};
          // Iterate over all keys in the raw data and normalize them
          for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              // Remove trailing "::" and trim whitespace from the key
              const normalizedKey = key.replace(/::\s*$/, '').trim();
              mappedData[normalizedKey] = data[key];
            }
          }

          // Ensure consistent field names for display and filtering
          return {
            id: doc.id,
            Company: mappedData.who, // Mapping 'who' from scraped data to 'Company' for display
            Date: mappedData.date,   // Using 'date' from scraped data
            Event: mappedData.event, // Using 'event' from scraped data
            Type: mappedData.type,   // Using 'type' from scraped data
            imageUrl: mappedData.imageUrl,
            // Add any other fields you might have in your Firestore documents
          };
        });

        // Populate allFactDates for calendar highlighting
        const datesWithFacts = new Set();
        allFactsData.forEach(fact => {
          if (fact.Date && typeof fact.Date === 'string') {
            const parts = fact.Date.split(/[-/]/); // Use character class for both '-' and '/'
            if (parts.length === 3) {
              datesWithFacts.add(`${parts[1]}-${parts[2]}`); // Store as "MM-DD"
            }
          }
        });
        setAllFactDates(datesWithFacts);

        // Filter facts for the currently selected date (month and day only)
        // eslint-disable-next-line no-unused-vars
        const [_, selectedMonth, selectedDay] = selectedDate.split('-'); // selectedYear is intentionally unused, renamed to _
        const filteredFacts = allFactsData.filter(fact => {
          if (fact.Date && typeof fact.Date === 'string') {
            const parts = fact.Date.split(/[-/]/); // Use character class for both '-' and '/'
            if (parts.length === 3) {
              const factMonth = parts[1];
              const factDay = parts[2];
              return factMonth === selectedMonth && factDay === selectedDay; // Only compare month and day
            }
          }
          return false;
        });

        // Sort by year
        filteredFacts.sort((a, b) => {
          const yearA = parseInt(a.Date?.split(/[-/]/)[0]);
          const yearB = parseInt(b.Date?.split(/[-/]/)[0]);
          if (!isNaN(yearA) && !isNaN(yearB)) {
            return yearA - yearB;
          }
          return (a.Event || '').localeCompare(b.Event || '');
        });

        setFacts(filteredFacts);
        setIsLoading(false);
      }, (err) => {
        console.error("Error fetching historical facts (onSnapshot):", err);
        setError(`Failed to load historical facts: ${err.message}`);
        setIsLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      // This catch block is for errors during the setup of the snapshot listener, not for data fetching errors within the listener.
      console.error("Error setting up historical facts listener:", err);
      setError(`Failed to initialize historical facts display: ${err.message}`);
      setIsLoading(false);
    }
  }, [db, selectedDate]); // Re-run when db or selectedDate changes

  // Fetch facts on component mount and when db changes
  useEffect(() => {
    if (db) {
      fetchFacts();
    }
  }, [db, fetchFacts]);

  // Calendar rendering logic
  const renderCalendarDays = () => {
    const year = currentMonthYear.getFullYear();
    const month = currentMonthYear.getMonth(); // 0-indexed

    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 for Sunday, 1 for Monday

    const days = [];
    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 text-center text-gray-400"></div>);
    }

    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDayFormatted = String(day).padStart(2, '0');
      const currentMonthFormatted = String(month + 1).padStart(2, '0');
      const dateKey = `${currentMonthFormatted}-${currentDayFormatted}`; // MM-DD format for lookup
      const hasFact = allFactDates.has(dateKey);

      const fullDateString = `${year}-${currentMonthFormatted}-${currentDayFormatted}`;
      const isSelected = selectedDate === fullDateString;

      days.push(
        <button
          key={day}
          className={`p-2 text-center rounded-md transition duration-150 ease-in-out
            ${isSelected ? 'bg-orange-500 text-white font-bold shadow-md' : 'bg-white text-gray-800 hover:bg-orange-100'}
            ${hasFact ? 'border-2 border-orange-500 font-semibold' : 'border border-gray-200'}
            ${!hasFact && !isSelected ? 'text-gray-600' : ''}
          `}
          onClick={() => setSelectedDate(fullDateString)}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonthYear(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonthYear(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"];

  return (
    <>
      <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">
        <h2 className="text-2xl font-bold text-orange-700 mb-4 text-center">On This Day in Electronics History</h2>

        <div className="mb-6 p-4 border border-gray-200 rounded-md bg-orange-50 space-y-3">
          {/* Calendar Navigation */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 rounded-full bg-orange-200 hover:bg-orange-300 transition duration-200"
              title="Previous Month"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-orange-800">
              {/* Removed year from display as requested */}
              {monthNames[currentMonthYear.getMonth()]}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 rounded-full bg-orange-200 hover:bg-orange-300 transition duration-200"
              title="Next Month"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2 text-sm font-medium text-center">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-orange-700 font-bold p-2">{day}</div>
            ))}
            {renderCalendarDays()}
          </div>

          <label htmlFor="factDate" className="block text-orange-800 text-sm font-medium mb-1 mt-4">
            Selected Date:
          </label>
          <input
            type="date"
            id="factDate"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
          />
          {isAllowedToModify && (
            <button
              onClick={handleScrapeFacts}
              className="w-full mt-4 px-4 py-2 bg-orange-600 text-white rounded-md font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isScrapingFacts}
            >
              {isScrapingFacts ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Scraping Facts...
                </div>
              ) : (
                'Scrape All Historical Facts (1930-1980)'
              )}
            </button>
          )}
          {scrapeFactsError && (
            <div className="mt-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <p className="font-semibold">Scraping Error:</p>
              <p>{scrapeFactsError}</p>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center text-orange-600">
            <svg className="animate-spin h-5 w-5 text-orange-600 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading historical facts...
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <p className="font-semibold">Error:</p>
            <p>{error}</p>
          </div>
        )}

        {!isLoading && facts.length === 0 && (
          <p className="text-gray-600 italic text-center mt-4">
            {isAllowedToModify
              ? `No historical facts found for this date. Click "Scrape All Historical Facts" to populate.`
              : `No historical facts found for this date.`
            }
          </p>
        )}

        {!isLoading && facts.length > 0 && (
          <div className="mt-6 space-y-4">
            {facts.map((fact) => (
              <div key={fact.id} className="bg-orange-50 p-4 rounded-lg border border-orange-200 shadow-sm">
                <p className="text-lg font-semibold text-orange-800 mb-2">
                  {/* Defensive display for Date */}
                  {fact.Date && typeof fact.Date === 'string' && fact.Date.split(/[-/]/).length === 3
                    ? `On ${fact.Date}`
                    : `On Unknown Date`}
                </p>
                {fact.Company && typeof fact.Company === 'string' && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong className="text-orange-700">Company:</strong> {fact.Company}
                  </p>
                )}
                {fact.Type && Array.isArray(fact.Type) && fact.Type.length > 0 && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong className="text-orange-700">Type:</strong> {fact.Type.join(', ')}
                  </p>
                )}
                <p className="text-gray-800 leading-relaxed mt-2">{fact.Event || 'No event description available.'}</p>
                {fact.imageUrl && (
                  <div
                    className="mt-3 flex justify-center cursor-pointer"
                    onClick={() => openImageModal(fact.imageUrl, fact.Event)}
                  >
                    <img
                      src={fact.imageUrl}
                      alt={fact.Event}
                      className="max-w-xs h-auto object-contain rounded-md border border-orange-300 shadow-md"
                      onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/300x150/ffedd5/d97706?text=Fact+Image`; }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// NEW: RepairLogBookSection Component
const RepairLogBookSection = ({ db, userId, isAllowedToModify, isAuthReady }) => {
  const [allRepairLogs, setAllRepairLogs] = useState([]); // Stores all logs
  const [lastLogEntryForSelectedDate, setLastLogEntryForSelectedDate] = useState(null); // Stores the single log for display
  const [newRepairDate, setNewRepairDate] = useState('');
  const [newEquipmentRepaired, setNewEquipmentRepaired] = useState('');
  const [newProblemDescription, setNewProblemDescription] = useState('');
  const [newSolution, setNewSolution] = useState('');
  const [newPartsUsed, setNewPartsUsed] = useState('');
  const [newTechnicianNotes, setNewTechnicianNotes] = useState('');
  const [isAddingLog, setIsAddingLog] = useState(false);
  const [logError, setLogError] = useState(null);
  const [editModeLog, setEditModeLog] = useState(false);
  const [currentLogId, setCurrentLogId] = useState(null);

  // Calendar states
  const [currentMonthYear, setCurrentMonthYear] = useState(new Date()); // Date object for calendar view
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD for log display
  const [datesWithLogs, setDatesWithLogs] = useState(new Set()); // Stores "YYYY-MM-DD" for highlighting

  // Fetch repair logs
  useEffect(() => {
    if (db) {
      const collectionAppId = db.app.options.projectId;
      const repairLogsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/repair_logs`);
      const q = query(repairLogsCollectionRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedLogs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort all logs by repairDate (descending), then by timestamp (descending)
        fetchedLogs.sort((a, b) => {
          const dateA = a.repairDate ? new Date(a.repairDate) : new Date(0);
          const dateB = b.repairDate ? new Date(b.repairDate) : new Date(0);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB.getTime() - dateA.getTime(); // Newest date first
          }
          return (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0); // Newest entry first for same date
        });

        setAllRepairLogs(fetchedLogs);

        // Populate datesWithLogs for calendar highlighting
        const logDates = new Set();
        fetchedLogs.forEach(log => {
          if (log.repairDate) {
            logDates.add(log.repairDate); // Store as YYYY-MM-DD
          }
        });
        setDatesWithLogs(logDates);

        // Update the displayed log for the current selected date
        // This will run when data changes, ensuring the displayed log is always up-to-date
        const logsForCurrentSelectedDate = fetchedLogs.filter(log => log.repairDate === selectedDate);
        setLastLogEntryForSelectedDate(logsForCurrentSelectedDate.length > 0 ? logsForCurrentSelectedDate[0] : null);

      }, (err) => {
        console.error("Error fetching repair logs:", err);
        setLogError(`Failed to load repair logs: ${err.message}`);
      });

      return () => unsubscribe();
    }
  }, [db, selectedDate]); // Add selectedDate to dependencies to re-filter when date changes

  // Update the displayed log when selectedDate changes (if allRepairLogs is already fetched)
  useEffect(() => {
    const logsForCurrentSelectedDate = allRepairLogs.filter(log => log.repairDate === selectedDate);
    setLastLogEntryForSelectedDate(logsForCurrentSelectedDate.length > 0 ? logsForCurrentSelectedDate[0] : null);
  }, [selectedDate, allRepairLogs]);


  // Add new repair log entry
  const handleAddRepairLog = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingLog(true);
    setLogError(null);

    if (!isAllowedToModify) {
      setLogError("You do not have permission to add repair logs.");
      setIsAddingLog(false);
      return;
    }

    if (!newRepairDate || !newEquipmentRepaired || !newProblemDescription || !newSolution) {
      setLogError("Repair Date, Equipment, Problem, and Solution are required fields.");
      setIsAddingLog(false);
      return;
    }

    try {
      const collectionAppId = db.app.options.projectId;
      const repairLogsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/repair_logs`);

      await addDoc(repairLogsCollectionRef, {
        repairDate: newRepairDate,
        equipmentRepaired: newEquipmentRepaired,
        problemDescription: newProblemDescription,
        solution: newSolution,
        partsUsed: newPartsUsed,
        technicianNotes: newTechnicianNotes,
        timestamp: serverTimestamp(),
        createdBy: userId
      });
      setNewRepairDate('');
      setNewEquipmentRepaired('');
      setNewProblemDescription('');
      setNewSolution('');
      setNewPartsUsed('');
      setNewTechnicianNotes('');
    } catch (err) {
      console.error("Error adding repair log:", err);
      setLogError(`Failed to add repair log: ${err.message}`);
    } finally {
      setIsAddingLog(false);
    }
  }, [db, userId, newRepairDate, newEquipmentRepaired, newProblemDescription, newSolution, newPartsUsed, newTechnicianNotes, isAllowedToModify]);

  // Update existing repair log entry
  const handleUpdateRepairLog = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingLog(true); // Reusing state for loading
    setLogError(null);

    if (!isAllowedToModify || !currentLogId) {
      setLogError("You do not have permission to update repair logs or no entry is selected.");
      setIsAddingLog(false);
      return;
    }

    const logToUpdate = allRepairLogs.find(item => item.id === currentLogId);
    if (!logToUpdate || logToUpdate.createdBy !== userId) {
        setLogError("You can only update entries you have created.");
        setIsAddingLog(false);
        return;
    }

    try {
      const collectionAppId = db.app.options.projectId;
      const logDocRef = doc(db, `artifacts/${collectionAppId}/public/data/repair_logs`, currentLogId);

      await updateDoc(logDocRef, {
        repairDate: newRepairDate,
        equipmentRepaired: newEquipmentRepaired,
        problemDescription: newProblemDescription,
        solution: newSolution,
        partsUsed: newPartsUsed,
        technicianNotes: newTechnicianNotes,
      });
      setNewRepairDate('');
      setNewEquipmentRepaired('');
      setNewProblemDescription('');
      setNewSolution('');
      setNewPartsUsed('');
      setNewTechnicianNotes('');
      setEditModeLog(false);
      setCurrentLogId(null);
    } catch (err) {
      console.error("Error updating repair log:", err);
      setLogError(`Failed to update repair log: ${err.message}`);
    } finally {
      setIsAddingLog(false);
    }
  }, [db, userId, currentLogId, newRepairDate, newEquipmentRepaired, newProblemDescription, newSolution, newPartsUsed, newTechnicianNotes, isAllowedToModify, allRepairLogs]);

  // Start edit mode for a repair log entry
  const startEditModeRepairLog = useCallback((log) => {
    if (!isAllowedToModify || log.createdBy !== userId) {
      setLogError("You do not have permission to edit this repair log.");
      return;
    }
    setEditModeLog(true);
    setCurrentLogId(log.id);
    setNewRepairDate(log.repairDate || '');
    setNewEquipmentRepaired(log.equipmentRepaired || '');
    setNewProblemDescription(log.problemDescription || '');
    setNewSolution(log.solution || '');
    setNewPartsUsed(log.partsUsed || '');
    setNewTechnicianNotes(log.technicianNotes || '');
    setLogError(null);
  }, [isAllowedToModify, userId]);

  // Cancel edit mode
  const cancelEditModeRepairLog = useCallback(() => {
    setEditModeLog(false);
    setCurrentLogId(null);
    setNewRepairDate('');
    setNewEquipmentRepaired('');
    setNewProblemDescription('');
    setNewSolution('');
    setNewPartsUsed('');
    setNewTechnicianNotes('');
    setLogError(null);
  }, []);

  // Delete repair log entry
  const handleDeleteRepairLog = useCallback(async (id) => {
    if (!isAllowedToModify) {
      setLogError("You do not have permission to delete repair logs.");
      return;
    }

    const logToDelete = allRepairLogs.find(item => item.id === id);
    if (!logToDelete || logToDelete.createdBy !== userId) {
        setLogError("You can only delete entries you have created.");
        return;
    }

    if (window.confirm("Are you sure you want to delete this repair log entry?")) {
      try {
        const collectionAppId = db.app.options.projectId;
        const logDocRef = doc(db, `artifacts/${collectionAppId}/public/data/repair_logs`, id);
        await deleteDoc(logDocRef);
        setLogError(null);
      } catch (err) {
        console.error("Error deleting repair log:", err);
        setLogError(`Failed to delete repair log: ${err.message}`);
      }
    }
  }, [db, userId, isAllowedToModify, allRepairLogs]);


  // Calendar rendering logic
  const renderCalendarDays = () => {
    const year = currentMonthYear.getFullYear();
    const month = currentMonthYear.getMonth(); // 0-indexed

    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 for Sunday, 1 for Monday

    const days = [];
    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 text-center text-gray-400"></div>);
    }

    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDayFormatted = String(day).padStart(2, '0');
      const currentMonthFormatted = String(month + 1).padStart(2, '0');
      const fullDateString = `${year}-${currentMonthFormatted}-${currentDayFormatted}`;
      const hasLog = datesWithLogs.has(fullDateString); // Check if this specific date has a log

      const isSelected = selectedDate === fullDateString;

      days.push(
        <button
          key={day}
          className={`p-2 text-center rounded-md transition duration-150 ease-in-out
            ${isSelected ? 'bg-teal-500 text-white font-bold shadow-md' : 'bg-white text-gray-800 hover:bg-teal-100'}
            ${hasLog ? 'border-2 border-teal-500 font-semibold' : 'border border-gray-200'}
            ${!hasLog && !isSelected ? 'text-gray-600' : ''}
          `}
          onClick={() => setSelectedDate(fullDateString)}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonthYear(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonthYear(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"];


  return (
    <div className="bg-white p-8 rounded-lg shadow-xl w-full lg:w-1/2 max-w-2xl border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-700 mb-3 text-center">Repair Log Book</h3>

      {logError && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p className="font-semibold">Error:</p>
          <p>{logError}</p>
        </div>
      )}

      {/* Add/Edit Repair Log Form */}
      {isAllowedToModify ? (
        <form onSubmit={editModeLog ? handleUpdateRepairLog : handleAddRepairLog} className="space-y-4 mb-8 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-lg font-semibold text-gray-700 mb-3">
            {editModeLog ? 'Edit Repair Log Entry' : 'Add New Repair Log Entry'}
          </h4>
          <div>
            <label htmlFor="repairDate" className="block text-gray-700 text-sm font-medium mb-1">
              Repair Date:
            </label>
            <input
              type="date"
              id="repairDate"
              value={newRepairDate}
              onChange={(e) => setNewRepairDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="equipmentRepaired" className="block text-gray-700 text-sm font-medium mb-1">
              Equipment Repaired (Make & Model):
            </label>
            <input
              type="text"
              id="equipmentRepaired"
              value={newEquipmentRepaired}
              onChange={(e) => setNewEquipmentRepaired(e.target.value)}
              placeholder="e.g., Fluke 117 Multimeter"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
              required
            />
          </div>
          <div>
            <label htmlFor="problemDescription" className="block text-gray-700 text-sm font-medium mb-1">
              Problem Description:
            </label>
            <textarea
              id="problemDescription"
              value={newProblemDescription}
              onChange={(e) => setNewProblemDescription(e.target.value)}
              placeholder="e.g., No power, display flickering"
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200 resize-y"
              required
            ></textarea>
          </div>
          <div>
            <label htmlFor="solution" className="block text-gray-700 text-sm font-medium mb-1">
              Solution:
            </label>
            <textarea
              id="solution"
              value={newSolution}
              onChange={(e) => setNewSolution(e.target.value)}
              placeholder="e.g., Replaced faulty power supply capacitor C101"
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200 resize-y"
              required
            ></textarea>
          </div>
          <div>
            <label htmlFor="partsUsed" className="block text-sm font-medium text-gray-700 mb-1">
              Parts Used (Optional):
            </label>
            <textarea
              id="partsUsed"
              value={newPartsUsed}
              onChange={(e) => setNewPartsUsed(e.target.value)}
              placeholder="e.g., 1x 1000uF 25V Electrolytic Capacitor, 2x 1N4007 Diode"
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200 resize-y"
            ></textarea>
          </div>
          <div>
            <label htmlFor="technicianNotes" className="block text-gray-700 text-sm font-medium mb-1">
              Technician Notes (Optional):
            </label>
            <textarea
              id="technicianNotes"
              value={newTechnicianNotes}
              onChange={(e) => setNewTechnicianNotes(e.target.value)}
              placeholder="Any additional observations or tips for future repairs."
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200 resize-y"
            ></textarea>
          </div>

          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-3 rounded-md font-semibold text-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAddingLog || !isAuthReady}
          >
            {isAddingLog ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
                Saving Log...
              </div>
            ) : (
              editModeLog ? 'Update Repair Log' : 'Add Repair Log'
            )}
          </button>
          {editModeLog && (
            <button
              type="button"
              onClick={cancelEditModeRepairLog}
              className="w-full bg-gray-500 text-white py-3 rounded-md font-semibold text-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300 ease-in-out transform hover:scale-105 mt-2"
            >
              Cancel Edit
            </button>
          )}
        </form>
      ) : (
        <div className="mb-8 p-4 border border-gray-200 rounded-md bg-yellow-50 text-yellow-800">
          <p className="font-semibold"></p>
        </div>
      )}

      {/* Calendar Section */}
      <div className="mb-6 p-4 border border-gray-200 rounded-md bg-teal-50 space-y-3">
        <h4 className="text-xl font-semibold text-gray-700 mb-2 text-center">Select a Date</h4>
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-full bg-teal-200 hover:bg-teal-300 transition duration-200"
            title="Previous Month"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h5 className="text-lg font-bold text-teal-800">
            {monthNames[currentMonthYear.getMonth()]} {currentMonthYear.getFullYear()}
          </h5>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-full bg-teal-200 hover:bg-teal-300 transition duration-200"
            title="Next Month"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-sm font-medium text-center">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-teal-700 font-bold p-2">{day}</div>
          ))}
          {renderCalendarDays()}
        </div>
      </div>

      {/* Display Last Log Entry for Selected Date */}
      <h4 className="text-xl font-semibold text-gray-700 mb-3 text-center">Latest Entry for (UTC) {selectedDate}</h4>
      {lastLogEntryForSelectedDate ? (
        <div className="bg-teal-100 border border-teal-300 rounded-lg shadow-sm p-4 relative text-left">
          <p className="text-lg font-bold text-teal-800 mb-1">Repair Date: {lastLogEntryForSelectedDate.repairDate}</p>
          <p className="text-md font-semibold text-gray-800 mb-1">Equipment: {lastLogEntryForSelectedDate.equipmentRepaired}</p>
          <p className="text-sm text-gray-700"><strong>Problem:</strong> {lastLogEntryForSelectedDate.problemDescription}</p>
          <p className="text-sm text-gray-700 mt-1"><strong>Solution:</strong> {lastLogEntryForSelectedDate.solution}</p>
          {lastLogEntryForSelectedDate.partsUsed && <p className="text-sm text-gray-600 mt-1"><strong>Parts Used:</strong> {lastLogEntryForSelectedDate.partsUsed}</p>}
          {lastLogEntryForSelectedDate.technicianNotes && <p className="text-sm text-gray-600 mt-1"><strong>Notes:</strong> {lastLogEntryForSelectedDate.technicianNotes}</p>}
          {isAllowedToModify && (
            <div className="absolute top-2 right-2 flex space-x-1">
              <button
                onClick={() => startEditModeRepairLog(lastLogEntryForSelectedDate)}
                className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-200"
                title="Edit Log"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zm-3.646 3.646l-2.828 2.828-1.414-1.414 2.828-2.828 1.414 1.414zm-5.657 5.657a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l1.414-1.414 4 4zM16 17H4v-2h12v2z" />
                </svg>
              </button>
              <button
                onClick={() => handleDeleteRepairLog(lastLogEntryForSelectedDate.id)}
                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-200"
                title="Delete Log"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm6 3a1 1 0 100 2v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600 italic text-center">No repair logs for this date.</p>
      )}
    </div>
  );
};


// Main App component
const App = () => {
  // State variables for repair assistant
  const [device, setDevice] = useState('');
  const [problem, setProblem] = useState('');
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // State variables for Firebase and equipment database
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null); // Store auth instance
  const [userId, setUserId] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState(null); // To display Google user's name
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentDescription, setNewEquipmentDescription] = useState('');
  const [newEquipmentImageUrl, setNewEquipmentImageUrl] = useState('');
  const [newEquipmentManufacturer, setNewEquipmentManufacturer] = useState('');
  const [newEquipmentModel, setNewEquipmentModel] = useState('');
  const [newEquipmentType, setNewEquipmentType] = useState('');
  const [newEquipmentSpecifications, setNewEquipmentSpecifications] = useState('');
  const [newEquipmentServiceManualUrl, setNewEquipmentServiceManualUrl] = useState('');
  // Removed newEquipmentBeforeImageUrl and newEquipmentAfterImageUrl from here as they are now in BeforeAfterGallerySection
  const [isAddingEquipment, setIsAddingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState(null);

  // State for editing equipment
  const [editMode, setEditMode] = useState(false);
  const [currentEquipmentId, setCurrentEquipmentId] = useState(null);

  // State for Failed Components
  const [failedComponentsList, setFailedComponentsList] = useState([]);
  const [newFailedComponentImageUrl, setNewFailedComponentImageUrl] = useState('');
  const [newFailedComponentDescription, setNewFailedComponentDescription] = useState('');
  const [isAddingFailedComponent, setIsAddingFailedComponent] = useState(false);
  const [failedComponentsError, setFailedComponentsError] = useState(null);
  const [editModeFailedComponent, setEditModeFailedComponent] = useState(false);
  const [currentFailedComponentId, setCurrentFailedComponentId] = useState(null);

  // State for managing view mode: 'repair', 'equipment', 'failedComponents', 'beforeAfterGallery', 'historicalFacts', 'repairLog'
  const [viewMode, setViewMode] = useState('repair');

  // New state variables for search filters
  const [searchManufacturer, setSearchManufacturer] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [searchType, setSearchType] = useState('');

  // State for Firebase Storage
  const [storage, setStorage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false); // For main equipment image
  const [uploadError, setUploadError] = useState(null); // For main equipment image
  // Removed before/after upload states from App as they are now managed in BeforeAfterGallerySection
  const [isUploadingFailedComponentImage, setIsUploadingFailedComponentImage] = useState(false); // For failed component image
  const [uploadFailedComponentError, setUploadFailedComponentError] = useState(null); // For failed component image

  // State for Image Modal (for full-screen view)
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalImageAltText, setModalImageAltText] = useState('');

  // State for scraping facts
  const [isScrapingFacts, setIsScrapingFacts] = useState(false);
  const [scrapeFactsError, setScrapeFactsError] = useState(null);


  // Define YOUR_GOOGLE_UID here. Replace "YOUR_ACTUAL_GOOGLE_UID_HERE" with your Google UID.
  // IMPORTANT: You MUST replace this placeholder with your actual Google UID from Firebase Console -> Authentication -> Users.
  // If you are logged in, your UID will also be logged to the console (F12 -> Console tab).
  const YOUR_GOOGLE_UID = "yN1hbYkxi1PtHx1fRU04DecQz4z1"; // <<< IMPORTANT: REPLACE THIS WITH YOUR UID

  // Derived state to check if the current user has modification permissions
  const isAllowedToModify = userId === YOUR_GOOGLE_UID;

  // Firebase config is now read from the global __firebase_config variable
  // This ensures the correct API key from your Firebase project is used for all services.
  const firebaseConfig = useMemo(() => { // Wrapped in useMemo
    const envConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : {};

    // Use __app_id for projectId, which is guaranteed to be correct in Canvas.
    // Fallback to envConfig.projectId or a placeholder if __app_id is not defined (e.g., local dev without Canvas globals).
    const projectId = typeof __app_id !== 'undefined' ? __app_id : envConfig.projectId || "electronics-repair-appv3";

    // Build the final config, prioritizing values from envConfig and then specific overrides
    return {
      apiKey: "AIzaSyDI2unXEx-wOZyGFFu18yao2XdH5K21Uyo", // <--- THIS IS YOUR ORIGINAL FIREBASE KEY 
  authDomain: "electronics-repair-appv3.firebaseapp.com", // Ensure this matches your project
  projectId: "electronics-repair-appv3", // Ensure this matches your project
  storageBucket: "electronics-repair-appv3.firebasestorage.app", // Ensure this matches your project
  messagingSenderId: "73987771086", // Get from Firebase Project Settings
  appId: "1:73987771086:web:739fab54670c2a675e1e9f", // Get from Firebase Project Settings
  measurementId: "G-YWEKQ6XQTX" // Get from Firebase Project Settings
    };
  }, []); // Empty dependency array means it only runs once

  // Initialize Firebase and set up authentication listener
  useEffect(() => {
    let unsubscribeFromAuth = null;
    try {
      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const authInstance = getAuth(app);
      const firebaseStorage = getStorage(app);

      setDb(firestoreDb);
      setAuth(authInstance); // Store auth instance
      setStorage(firebaseStorage);

      unsubscribeFromAuth = onAuthStateChanged(authInstance, (user) => {
        if (user) {
          setUserId(user.uid);
          setUserDisplayName(user.displayName || user.email || 'Google User');
          // --- IMPORTANT DEBUGGING LOG ---
          console.log("Logged in User ID (UID):", user.uid);
          // -------------------------------
        } else {
          setUserId(null);
          setUserDisplayName(null);
        }
        setIsAuthReady(true);
      });

      return () => {
        if (unsubscribeFromAuth) {
          unsubscribeFromAuth();
        }
      };
    } catch (err) {
      console.error("Error initializing Firebase:", err);
      setError(`Firebase initialization failed: ${err.message}`);
    }
  }, [firebaseConfig]); // Added firebaseConfig to dependency array

  // Google Sign-In handler
  const handleGoogleSignIn = useCallback(async () => {
    if (!auth) {
      setError("Firebase Auth not initialized.");
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setError(null);
    } catch (signInError) {
      console.error("Error signing in with Google:", signInError);
      setError(`Google Sign-In failed: ${signInError.message}`);
    }
  }, [auth]);

  // Sign Out handler
  const handleSignOut = useCallback(async () => {
    if (!auth) return;
    try {
      await signOut(auth);
      setError(null);
    } catch (signOutError) {
      console.error("Error signing out:", signOutError);
      setError(`Sign Out failed: ${signOutError.message}`);
    }
  }, [auth]);

  // Fetch and filter equipment list when db or search terms change
  useEffect(() => {
    if (db) {
      const collectionAppId = db.app.options.projectId;
      const equipmentCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/equipment`);
      const q = query(equipmentCollectionRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allEquipmentData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const filteredEquipment = allEquipmentData.filter(equipment => {
          const matchesManufacturer = searchManufacturer ? equipment.manufacturer?.toLowerCase().includes(searchManufacturer.toLowerCase()) : true;
          const matchesModel = searchModel ? equipment.model?.toLowerCase().includes(searchModel.toLowerCase()) : true;
          const matchesType = searchType ? equipment.type?.toLowerCase().includes(searchType.toLowerCase()) : true;
          return matchesManufacturer && matchesModel && matchesType;
        });

        filteredEquipment.sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));
        setEquipmentList(filteredEquipment);
      }, (err) => {
        console.error("Error fetching equipment:", err);
        setEquipmentError(`Failed to load equipment: ${err.message}`);
      });

      return () => unsubscribe();
    }
  }, [db, searchManufacturer, searchModel, searchType]);

  // Fetch failed components list
  useEffect(() => {
    if (db) {
      const collectionAppId = db.app.options.projectId;
      const failedComponentsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/failed_components`);
      const q = query(failedComponentsCollectionRef);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allFailedComponents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        allFailedComponents.sort((a, b) => (b.timestamp?.toDate() || 0) - (a.timestamp?.toDate() || 0));
        setFailedComponentsList(allFailedComponents);
      }, (err) => {
        console.error("Error fetching failed components:", err);
        setFailedComponentsError(`Failed to load failed components: ${err.message}`);
      });

      return () => unsubscribe();
    }
  }, [db]);


  // Function to handle form submission and call Gemini API
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponse(null);
    setError(null);

    try {
      const matchedEquipment = equipmentList.find(
        (eq) => eq.name.toLowerCase() === device.toLowerCase() ||
                (eq.manufacturer && eq.model && `${eq.manufacturer} ${eq.model}`.toLowerCase() === device.toLowerCase())
      );

      let specificationsForPrompt = '';
      if (matchedEquipment && matchedEquipment.specifications) {
        specificationsForPrompt = `Its known specifications are: "${matchedEquipment.specifications}".`;
      } else {
        specificationsForPrompt = `Please infer its typical use and fundamental specifications (like maximum frequency range, bandwidth, or power output) based on your knowledge.`;
      }

      const prompt = `An electronic test equipment, "${device}", is experiencing the problem: "${problem}".
      ${specificationsForPrompt}

      Please act as an expert electronics repair technician. Based on this information and the described problem:

      1.  **CRITICAL SAFETY WARNING:** State clearly and prominently that electronic test equipment can contain and produce lethal voltages, even when unplugged, due to stored energy in capacitors. Emphasize that repairs should ONLY be attempted by qualified personnel who understand electrical safety procedures.
      2.  **Capability Check & Initial Assessment:** Analyze the provided or inferred specifications. Does the described problem scenario potentially violate the equipment's fundamental capabilities or typical usage (e.g., is the user trying to measure a signal outside its frequency range, apply a voltage beyond its maximum input, or draw current beyond its rating)? Explain your reasoning clearly, referring to specific specifications if available. If the device is not well-known or you cannot infer its capabilities, state that.
      3.  **Most Likely Causes:** Identify the top 3-5 most likely causes for this specific fault in this type of equipment, considering its design and common failure points.
      4.  **Detailed Diagnostic & Repair Steps:** Provide a step-by-step guide (5-10 steps) on how to systematically diagnose the problem, isolate the faulty component/area, and perform the necessary repair. Be precise and include safety considerations where applicable.
      5.  **Required Tools & Equipment:** List all essential tools and additional test equipment needed for the diagnosis and repair process.

      Please format your response clearly with bold headings for each section.`;

      const chatHistory = [{ role: "user", parts: [{ text: prompt }] }];
      const payload = { contents: chatHistory };

      // Use the Firebase API key from firebaseConfig for Gemini API calls
      const geminiApiKey = firebaseConfig.apiKey;
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;

      const apiResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!apiResponse.ok) {
        const errorData = await apiResponse.json();
        throw new Error(`API error: ${errorData.error.message || apiResponse.statusText}`);
      }

      const result = await apiResponse.json();

      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        const text = result.candidates[0].content.parts[0].text;
        setResponse(text);
      } else {
        setResponse("No specific repair suggestions could be generated. Please try rephrasing your query.");
      }
    } catch (err) {
      console.error("Error calling Gemini API:", err);
      setError(`Failed to get repair suggestions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [device, problem, equipmentList, firebaseConfig.apiKey]);

  // Function to handle image file upload for equipment (main)
  // Before/After handled by BeforeAfterGallerySection now
  const handleImageUpload = useCallback(async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Client-side permission check for upload
    if (!isAllowedToModify) {
      if (imageType === 'main') setUploadError("You do not have permission to upload main images.");
      return;
    }

    let setIsUploading, setUploadErrorState, setImageUrlState;
    let storagePath;

    if (imageType === 'main') {
      setIsUploading = setIsUploadingImage;
      setUploadErrorState = setUploadError;
      setImageUrlState = setNewEquipmentImageUrl;
      storagePath = `artifacts/${firebaseConfig.projectId}/public/data/equipment_images/${userId}/main_${file.name}`;
    } else {
      console.error("Invalid imageType provided to handleImageUpload:", imageType);
      return;
    }

    setIsUploading(true);
    setUploadErrorState(null);

    const imageRef = ref(storage, storagePath);

    try {
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      setImageUrlState(downloadURL); // Update the respective state variable
      setUploadErrorState(null);
    } catch (err) {
      console.error(`Error uploading ${imageType} image:`, err);
      setUploadErrorState(`Image upload failed: ${err.message}. Make sure you are signed in with the correct account.`);
      setImageUrlState(''); // Clear the input if upload fails
    } finally {
      setIsUploading(false);
    }
  }, [db, userId, storage, isAllowedToModify, setNewEquipmentImageUrl, setIsUploadingImage, setUploadError, firebaseConfig.projectId]);

  // Function to handle FAILED COMPONENT image file upload (separate handler)
  const handleFailedComponentImageUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAllowedToModify) {
      setUploadFailedComponentError("You do not have permission to upload images.");
      return;
    }

    setIsUploadingFailedComponentImage(true);
    setUploadFailedComponentError(null);

    const collectionAppId = firebaseConfig.projectId;
    const storagePath = `artifacts/${collectionAppId}/public/data/failed_components_images/${userId}/${file.name}`;
    const imageRef = ref(storage, storagePath);

    try {
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      setNewFailedComponentImageUrl(downloadURL);
      setUploadFailedComponentError(null);
    } catch (err) {
      console.error("Error uploading failed component image:", err);
      setUploadFailedComponentError(`Image upload failed: ${err.message}. Make sure you are signed in with the correct account.`);
      setNewFailedComponentImageUrl('');
    } finally {
      setIsUploadingFailedComponentImage(false);
    }
  }, [db, userId, storage, isAllowedToModify, setNewFailedComponentImageUrl, firebaseConfig.projectId]);


  // Function to handle adding new equipment to Firestore
  const handleAddEquipment = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingEquipment(true);
    setEquipmentError(null);

    // Client-side permission check
    if (!isAllowedToModify) {
      setEquipmentError("You do not have permission to add equipment. Please sign in with the authorized Google account.");
      setIsAddingEquipment(false);
      return;
    }

    if (!newEquipmentName.trim()) {
      setEquipmentError("Equipment name cannot be empty.");
      setIsAddingEquipment(false);
      return;
    }

    try {
      const collectionAppId = firebaseConfig.projectId;
      // Public collection path for write operations
      const equipmentCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/equipment`);

      await addDoc(equipmentCollectionRef, {
        name: newEquipmentName,
        description: newEquipmentDescription,
        imageUrl: newEquipmentImageUrl || `https://placehold.co/192x96/aabbcc/ffffff?text=${encodeURIComponent(newEquipmentName.substring(0, 10))}`,
        manufacturer: newEquipmentManufacturer,
        model: newEquipmentModel,
        type: newEquipmentType,
        specifications: newEquipmentSpecifications,
        serviceManualUrl: newEquipmentServiceManualUrl,
        // beforeImageUrl and afterImageUrl are NO LONGER part of equipment document
        timestamp: serverTimestamp(),
        // Store the userId (Google UID) with the document, for server-side security rules
        createdBy: userId
      });
      setNewEquipmentName('');
      setNewEquipmentDescription('');
      setNewEquipmentImageUrl('');
      setNewEquipmentManufacturer('');
      setNewEquipmentModel('');
      setNewEquipmentType('');
      setNewEquipmentSpecifications('');
      setNewEquipmentServiceManualUrl('');
    } catch (err) {
      console.error("Error adding equipment:", err);
      setEquipmentError(`Failed to add equipment: ${err.message}`);
    } finally {
      setIsAddingEquipment(false);
    }
  }, [db, userId, newEquipmentName, newEquipmentDescription, newEquipmentImageUrl, newEquipmentManufacturer, newEquipmentModel, newEquipmentType, newEquipmentSpecifications, newEquipmentServiceManualUrl, isAllowedToModify, firebaseConfig.projectId]);

  // Function to handle updating existing equipment in Firestore
  const handleUpdateEquipment = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingEquipment(true);
    setEquipmentError(null);

    // Client-side permission check
    if (!isAllowedToModify || !currentEquipmentId) {
      setEquipmentError("You do not have permission to update equipment or no equipment is selected.");
      setIsAddingEquipment(false);
      return;
    }

    if (!newEquipmentName.trim()) {
      setEquipmentError("Equipment name cannot be empty.");
      setIsAddingEquipment(false);
      return;
    }

    // Find the equipment to ensure the 'createdBy' matches the current user for server-side rule
    const equipmentToUpdate = equipmentList.find(item => item.id === currentEquipmentId);
    if (!equipmentToUpdate || equipmentToUpdate.createdBy !== userId) {
        setEquipmentError("You can only update equipment you have created.");
        setIsAddingEquipment(false);
        return;
    }

    try {
      const collectionAppId = firebaseConfig.projectId;
      // Public collection path for write operations
      const equipmentDocRef = doc(db, `artifacts/${collectionAppId}/public/data/equipment`, currentEquipmentId);

      await updateDoc(equipmentDocRef, {
        name: newEquipmentName,
        description: newEquipmentDescription,
        imageUrl: newEquipmentImageUrl || `https://placehold.co/192x96/aabbcc/ffffff?text=${encodeURIComponent(newEquipmentName.substring(0, 10))}`,
        manufacturer: newEquipmentManufacturer,
        model: newEquipmentModel,
        type: newEquipmentType,
        specifications: newEquipmentSpecifications,
        serviceManualUrl: newEquipmentServiceManualUrl,
        // beforeImageUrl and afterImageUrl are NO LONGER part of equipment document
        // createdBy is not updated, it remains the original creator's UID
      });
      setNewEquipmentName('');
      setNewEquipmentDescription('');
      setNewEquipmentImageUrl('');
      setNewEquipmentManufacturer('');
      setNewEquipmentModel('');
      setNewEquipmentType('');
      setNewEquipmentSpecifications('');
      setNewEquipmentServiceManualUrl('');
      setEditMode(false);
      setCurrentEquipmentId(null);
    } catch (err) {
      console.error("Error updating equipment:", err);
      setEquipmentError(`Failed to update equipment: ${err.message}`);
    } finally {
      setIsAddingEquipment(false);
    }
  }, [db, userId, currentEquipmentId, newEquipmentName, newEquipmentDescription, newEquipmentImageUrl, newEquipmentManufacturer, newEquipmentModel, newEquipmentType, newEquipmentSpecifications, newEquipmentServiceManualUrl, isAllowedToModify, equipmentList, firebaseConfig.projectId]);

  // Function to set up edit mode for a selected equipment item
  const startEditMode = useCallback((equipment) => {
    // Client-side permission check
    if (!isAllowedToModify || equipment.createdBy !== userId) {
      setEquipmentError("You do not have permission to edit this equipment.");
      return;
    }
    setEditMode(true);
    setCurrentEquipmentId(equipment.id);
    setNewEquipmentName(equipment.name);
    setNewEquipmentDescription(equipment.description);
    setNewEquipmentImageUrl(equipment.imageUrl);
    setNewEquipmentManufacturer(equipment.manufacturer);
    setNewEquipmentModel(equipment.model);
    setNewEquipmentType(equipment.type);
    setNewEquipmentSpecifications(equipment.specifications);
    setNewEquipmentServiceManualUrl(equipment.serviceManualUrl || '');
    setEquipmentError(null);
  }, [isAllowedToModify, userId]);

  // Function to cancel edit mode
  const cancelEditMode = useCallback(() => {
    setEditMode(false);
    setCurrentEquipmentId(null);
    setNewEquipmentName('');
    setNewEquipmentDescription('');
    setNewEquipmentImageUrl('');
    setNewEquipmentManufacturer('');
    setNewEquipmentModel('');
    setNewEquipmentType('');
    setNewEquipmentSpecifications('');
    setNewEquipmentServiceManualUrl('');
    setEquipmentError(null);
  }, []);

  // Function to handle deleting equipment from Firestore
  const handleDeleteEquipment = useCallback(async (id) => {
    // Client-side permission check
    if (!isAllowedToModify) {
      setEquipmentError("You do not have permission to delete equipment.");
      return;
    }

    // Find the equipment to ensure the 'createdBy' matches the current user for server-side rule
    const equipmentToDelete = equipmentList.find(item => item.id === id);
    if (!equipmentToDelete || equipmentToDelete.createdBy !== userId) {
        setEquipmentError("You can only delete equipment you have created.");
        return;
    }

    if (window.confirm("Are you sure you want to delete this equipment entry and its associated images?")) {
      try {
        const collectionAppId = firebaseConfig.projectId;
        const equipmentDocRef = doc(db, `artifacts/${collectionAppId}/public/data/equipment`, id);

        // Get the document to retrieve image URLs before deleting
        const docSnapshot = await db.getDoc(equipmentDocRef);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const imageUrlsToDelete = [];
          if (data.imageUrl) imageUrlsToDelete.push(data.imageUrl);

          // Delete images from Storage
          const storageInstance = getStorage(db.app);
          for (const url of imageUrlsToDelete) {
            try {
              const imageRef = ref(storageInstance, url);
              await deleteObject(imageRef);
              console.log(`Deleted image from storage: ${url}`);
            } catch (storageErr) {
              console.warn(`Could not delete image ${url} from storage:`, storageErr);
              // Continue with Firestore deletion even if image deletion fails
            }
          }
        }

        // Delete the document from Firestore
        await deleteDoc(equipmentDocRef);
        setEquipmentError(null);
        console.log(`Successfully deleted equipment document: ${id}`);
      } catch (err) {
        console.error("Error deleting equipment:", err);
        setEquipmentError(`Failed to delete equipment: ${err.message}`);
      }
    }
  }, [db, userId, isAllowedToModify, equipmentList, firebaseConfig.projectId]);

  // Functions for Failed Components
  const handleAddFailedComponent = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingFailedComponent(true);
    setFailedComponentsError(null);

    if (!isAllowedToModify) {
      setFailedComponentsError("You do not have permission to add failed components. Please sign in with the authorized Google account.");
      setIsAddingFailedComponent(false);
      return;
    }

    // Validate that an image URL exists (either from upload or previously set in edit mode)
    if (!newFailedComponentImageUrl.trim()) {
      setFailedComponentsError("Please upload an image for the failed component.");
      setIsAddingFailedComponent(false);
      return;
    }

    try {
      const collectionAppId = firebaseConfig.projectId;
      const failedComponentsCollectionRef = collection(db, `artifacts/${collectionAppId}/public/data/failed_components`);

      await addDoc(failedComponentsCollectionRef, {
        imageUrl: newFailedComponentImageUrl,
        description: newFailedComponentDescription,
        timestamp: serverTimestamp(),
        createdBy: userId
      });
      setNewFailedComponentImageUrl('');
      setNewFailedComponentDescription('');
    } catch (err) {
      console.error("Error adding failed component:", err);
      setFailedComponentsError(`Failed to add failed component: ${err.message}`);
    } finally {
      setIsAddingFailedComponent(false);
    }
  }, [db, userId, newFailedComponentImageUrl, newFailedComponentDescription, isAllowedToModify, firebaseConfig.projectId]);

  const handleUpdateFailedComponent = useCallback(async (e) => {
    e.preventDefault();
    setIsAddingFailedComponent(true);
    setFailedComponentsError(null);

    if (!isAllowedToModify || !currentFailedComponentId) {
      setFailedComponentsError("You do not have permission to update failed components or no component is selected.");
      setIsAddingFailedComponent(false);
      return;
    }

    // Validate that an image URL exists (either from upload or previously set in edit mode)
    if (!newFailedComponentImageUrl.trim()) {
      setFailedComponentsError("Please upload an image for the failed component.");
      setIsAddingFailedComponent(false);
      return;
    }

    const componentToUpdate = failedComponentsList.find(item => item.id === currentFailedComponentId);
    if (!componentToUpdate || componentToUpdate.createdBy !== userId) {
        setFailedComponentsError("You can only update components you have created.");
        return;
    }

    try {
      const collectionAppId = firebaseConfig.projectId;
      const componentDocRef = doc(db, `artifacts/${collectionAppId}/public/data/failed_components`, currentFailedComponentId);

      await updateDoc(componentDocRef, {
        imageUrl: newFailedComponentImageUrl,
        description: newFailedComponentDescription,
      });
      setNewFailedComponentImageUrl('');
      setNewFailedComponentDescription('');
      setEditModeFailedComponent(false);
      setCurrentFailedComponentId(null);
    } catch (err) {
      console.error("Error updating failed component:", err);
      setFailedComponentsError(`Failed to update failed component: ${err.message}`);
    } finally {
      setIsAddingFailedComponent(false);
    }
  }, [db, userId, currentFailedComponentId, newFailedComponentImageUrl, newFailedComponentDescription, isAllowedToModify, failedComponentsList, firebaseConfig.projectId]);

  const startEditModeFailedComponent = useCallback((component) => {
    if (!isAllowedToModify || component.createdBy !== userId) {
      setFailedComponentsError("You do not have permission to edit this component.");
      return;
    }
    setEditModeFailedComponent(true);
    setCurrentFailedComponentId(component.id);
    setNewFailedComponentImageUrl(component.imageUrl);
    setNewFailedComponentDescription(component.description || '');
    setFailedComponentsError(null);
  }, [isAllowedToModify, userId]);

  const cancelEditModeFailedComponent = useCallback(() => {
    setEditModeFailedComponent(false);
    setCurrentFailedComponentId(null);
    setNewFailedComponentImageUrl('');
    setNewFailedComponentDescription('');
    setFailedComponentsError(null);
  }, []);

  const handleDeleteFailedComponent = useCallback(async (id) => {
    if (!isAllowedToModify) {
      setFailedComponentsError("You do not have permission to delete failed components.");
      return;
    }

    const componentToDelete = failedComponentsList.find(item => item.id === id);
    if (!componentToDelete || componentToDelete.createdBy !== userId) {
        setFailedComponentsError("You can only delete components you have created.");
        return;
    }

    if (window.confirm("Are you sure you want to delete this failed component entry and its associated image?")) {
      try {
        const collectionAppId = firebaseConfig.projectId;
        const componentDocRef = doc(db, `artifacts/${collectionAppId}/public/data/failed_components`, id);

        // Get the document to retrieve image URL before deleting
        const docSnapshot = await db.getDoc(componentDocRef);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          if (data.imageUrl) {
            try {
              const storageInstance = getStorage(db.app);
              const imageRef = ref(storageInstance, data.imageUrl);
              await deleteObject(imageRef);
              console.log(`Deleted failed component image from storage: ${data.imageUrl}`);
            } catch (storageErr) {
              console.warn(`Could not delete failed component image ${data.imageUrl} from storage:`, storageErr);
            }
          }
        }

        await deleteDoc(componentDocRef);
        setFailedComponentsError(null);
      } catch (err) {
        console.error("Error deleting failed component:", err);
        setFailedComponentsError(`Failed to delete failed component: ${err.message}`);
      }
    }
  }, [db, userId, isAllowedToModify, failedComponentsList, firebaseConfig.projectId]);

  // Functions for Image Modal (for full-screen view)
  const openImageModal = useCallback((imageUrl, altText) => {
    setModalImageUrl(imageUrl);
    setModalImageAltText(altText);
    setShowImageModal(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setModalImageUrl('');
    setModalImageAltText('');
  }, []);

  // NEW: Function to handle scraping facts via Cloud Function
  const handleScrapeFacts = useCallback(async () => {
    if (!isAuthReady || !userId) {
      setScrapeFactsError("User not authenticated. Please sign in to trigger scraping.");
      return;
    }
    if (!isAllowedToModify) {
      setScrapeFactsError("You must be signed in with the authorized Google account to trigger scraping.");
      return;
    }

    setIsScrapingFacts(true);
    setScrapeFactsError(null);

    try {
      const cloudFunctionUrl = `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/scrapeHistoricalFacts`;

      // Get ID token if user is authenticated, otherwise it will be null
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;

      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Only include Authorization header if idToken exists
          'Authorization': idToken ? `Bearer ${idToken}` : ''
        },
        body: JSON.stringify({
          userId: userId,
          appId: firebaseConfig.projectId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API error: ${errorData.message || response.statusText}`);
      }

      const result = await response.json();
      console.log("Cloud Function response:", result);
      setScrapeFactsError(result.status === 'success' ? null : `Cloud Function Error: ${result.message}`);

      // After triggering, the frontend just waits for Firestore changes via onSnapshot
      // No need to parse facts here, as the Cloud Function writes directly to Firestore.

    } catch (err) {
      console.error("Error triggering Cloud Function:", err);
      setScrapeFactsError(`Failed to trigger scraping: ${err.message}. Please ensure your Cloud Function is deployed and accessible.`);
    } finally {
      setIsScrapingFacts(false);
    }
  }, [isAuthReady, userId, isAllowedToModify, auth, firebaseConfig.projectId]); // Add dependencies

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4 font-sans gap-6">
      <div className="w-full flex flex-col items-center mt-16">
        {/* Navigation Buttons for View Mode */}
        <div className="w-full flex flex-wrap justify-center gap-2 mb-4 z-10">
          <button
            onClick={() => setViewMode('repair')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'repair' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-100'
            }`}
          >
            Repair Assistant
          </button>
          <button
            onClick={() => setViewMode('equipment')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'equipment' ? 'bg-green-600 text-white shadow-lg' : 'bg-white text-green-700 border border-green-300 hover:bg-green-100'
            }`}
          >
            My Equipment Database
          </button>
          <button
            onClick={() => setViewMode('failedComponents')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'failedComponents' ? 'bg-purple-600 text-white shadow-lg' : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-100'
            }`}
          >
            Headscratcher's Gallery
          </button>
          <button
            onClick={() => setViewMode('beforeAfterGallery')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'beforeAfterGallery' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-100'
            }`}
          >
            Before & After Gallery
          </button>
          <button
            onClick={() => setViewMode('repairLog')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'repairLog' ? 'bg-teal-600 text-white shadow-lg' : 'bg-white text-teal-700 border border-teal-300 hover:bg-teal-100'
            }`}
          >
            Repair Log Book
          </button>
          <button
            onClick={() => setViewMode('historicalFacts')}
            className={`px-3 py-2 sm:px-6 sm:py-3 rounded-full font-semibold text-lg transition duration-300 ease-in-out transform hover:scale-105 ${
              viewMode === 'historicalFacts' ? 'bg-orange-600 text-white shadow-lg' : 'bg-white text-orange-700 border border-orange-300 hover:bg-orange-100'
            }`}
          >
            Historical Facts
          </button>
        </div>

        {/* User ID Display and Sign-in/Sign-out */}
        <div className="w-full text-center mb-6">
          <AuthStatusDisplay
            userId={userId}
            userDisplayName={userDisplayName}
            handleGoogleSignIn={handleGoogleSignIn}
            handleSignOut={handleSignOut}
            isAuthReady={isAuthReady}
            isAllowedToModify={isAllowedToModify}
          />
        </div>

        {/* Conditional Rendering of Sections */}
        {viewMode === 'repair' && (
          <RepairAssistantSection
            device={device}
            setDevice={setDevice}
            problem={problem}
            setProblem={setProblem}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            error={error}
            response={response}
            isAuthReady={isAuthReady}
          />
        )}

        {viewMode === 'equipment' && (
          <EquipmentDatabaseSection
            equipmentError={equipmentError}
            editMode={editMode}
            handleUpdateEquipment={handleUpdateEquipment}
            handleAddEquipment={handleAddEquipment}
            newEquipmentName={newEquipmentName}
            setNewEquipmentName={setNewEquipmentName}
            newEquipmentDescription={newEquipmentDescription}
            setNewEquipmentDescription={setNewEquipmentDescription}
            newEquipmentImageUrl={newEquipmentImageUrl}
            setNewEquipmentImageUrl={setNewEquipmentImageUrl}
            newEquipmentManufacturer={newEquipmentManufacturer}
            setNewEquipmentManufacturer={setNewEquipmentManufacturer}
            newEquipmentModel={newEquipmentModel}
            setNewEquipmentModel={setNewEquipmentModel}
            newEquipmentType={newEquipmentType}
            setNewEquipmentType={setNewEquipmentType}
            newEquipmentSpecifications={newEquipmentSpecifications}
            setNewEquipmentSpecifications={setNewEquipmentSpecifications}
            newEquipmentServiceManualUrl={newEquipmentServiceManualUrl}
            setNewEquipmentServiceManualUrl={setNewEquipmentServiceManualUrl}
            isAddingEquipment={isAddingEquipment}
            isAuthReady={isAuthReady}
            handleImageUpload={handleImageUpload}
            isUploadingImage={isUploadingImage}
            uploadError={uploadError}
            cancelEditMode={cancelEditMode}
            equipmentList={equipmentList}
            startEditMode={startEditMode}
            handleDeleteEquipment={handleDeleteEquipment}
            searchManufacturer={searchManufacturer}
            setSearchManufacturer={setSearchManufacturer}
            searchModel={searchModel}
            setSearchModel={setSearchModel}
            searchType={searchType}
            setSearchType={setSearchType}
            isAllowedToModify={isAllowedToModify}
            openImageModal={openImageModal}
          />
        )}

        {viewMode === 'failedComponents' && (
          <FailedComponentsSection
            failedComponentsError={failedComponentsError}
            editModeFailedComponent={editModeFailedComponent}
            handleUpdateFailedComponent={handleUpdateFailedComponent}
            handleAddFailedComponent={handleAddFailedComponent}
            newFailedComponentImageUrl={newFailedComponentImageUrl}
            setNewFailedComponentImageUrl={setNewFailedComponentImageUrl}
            newFailedComponentDescription={newFailedComponentDescription}
            setNewFailedComponentDescription={setNewFailedComponentDescription}
            isAddingFailedComponent={isAddingFailedComponent}
            isAuthReady={isAuthReady}
            handleFailedComponentImageUpload={handleFailedComponentImageUpload}
            isUploadingFailedComponentImage={isUploadingFailedComponentImage}
            uploadFailedComponentError={uploadFailedComponentError}
            cancelEditModeFailedComponent={cancelEditModeFailedComponent}
            failedComponentsList={failedComponentsList}
            startEditModeFailedComponent={startEditModeFailedComponent}
            handleDeleteFailedComponent={handleDeleteFailedComponent}
            isAllowedToModify={isAllowedToModify}
            openImageModal={openImageModal}
          />
        )}

        {viewMode === 'beforeAfterGallery' && (
          <BeforeAfterGallerySection
            db={db}
            userId={userId}
            isAllowedToModify={isAllowedToModify}
            openImageModal={openImageModal}
            storage={storage}
            isAuthReady={isAuthReady}
          />
        )}

        {viewMode === 'repairLog' && (
          <RepairLogBookSection
            db={db}
            userId={userId}
            isAllowedToModify={isAllowedToModify}
            isAuthReady={isAuthReady}
          />
        )}

        {viewMode === 'historicalFacts' && (
          <HistoricalFactsSection
            db={db}
            openImageModal={openImageModal}
            handleScrapeFacts={handleScrapeFacts}
            isScrapingFacts={isScrapingFacts}
            scrapeFactsError={scrapeFactsError}
            isAllowedToModify={isAllowedToModify}
          />
        )}
      </div>

      {/* Image Modal - Rendered conditionally */}
      {showImageModal && (
        <ImageModal
          imageUrl={modalImageUrl}
          altText={modalImageAltText}
          onClose={closeImageModal}
        />
      )}

      {/* Global Disclaimer Footer */}
      <footer className="w-full bg-gray-800 text-white text-center p-5 mt-auto rounded-t-lg shadow-inner">
        <div className="max-w-full mx-auto text-sm leading-relaxed">
          <p className="mb-3">
            <strong>Trademark Disclaimer:</strong> All product names, logos, and brands (e.g., Agilent, Fluke, HP, Tektronix) mentioned on this site are the property of their respective owners. All company, product, and service names used in this website are for identification purposes only. Use of these names, logos, and brands does not imply endorsement. 
          </p>
          <p className="text-red-400 font-bold text-base mb-3">
            <strong>Critical Safety Warning:</strong> The Repair Assistant provides AI-generated suggestions for informational purposes only. It does not claim to be correct, complete, or suitable for any specific repair. Electronic test equipment can contain and produce lethal voltages, even when unplugged, due to stored energy in capacitors.
          </p>
          <p className="text-red-400 font-bold text-base">
            **ANY ADVICE GIVEN CAN LEAD TO SERIOUS INJURY, ELECTROCUTION, OR DEATH, AND MAY CAUSE FURTHER DAMAGE TO EQUIPMENT.** Repairs should ONLY be attempted by qualified personnel with a solid understanding of electrical safety procedures, proper tools, and the inherent risks involved. The creators and providers of this application assume no liability for any damages or injuries incurred.
          </p>
          <p className="w-full bg-gray-800 text-white text-center p-3 mt-auto rounded-t-lg shadow-inner">
            2025 Electronics Repair App. All images and content are for informational purposes only and may not be reproduced or used without explicit permission.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;